import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { type PlainWorkflow, type PlainWorkflowExecution, workflowQueryKey } from "@/queries/automation-queries";
import { defaultRefetchLists, defaultRetry } from "@/queries/errors";
import { autoPaginate } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useQueries, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { PaginationRequest, Sort, SortOrder } from "gitpod-next-api/gitpod/v1/pagination_pb";
import {
    CreateWebhookRequest,
    DeleteWebhookRequest,
    GetWebhookRequest,
    GetWebhookSecretRequest,
    ListWebhooksRequest,
    ListWebhookWorkflowsRequest,
    RotateWebhookSecretRequest,
    UpdateWebhookRequest,
    type Webhook,
    type WebhookProvider,
    type WebhookType,
} from "gitpod-next-api/gitpod/v1/webhook_pb";
import { WorkflowExecutionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";

export const webhookQueryKey = {
    list: () => keyWithPrincipal(["webhooks", "list"]),
    get: (webhookId?: string) => keyWithPrincipal(["webhooks", { webhookId }]),
    secret: (webhookId?: string) => keyWithPrincipal(["webhooks", "secret", { webhookId }]),
    workflows: (webhookId?: string) => keyWithPrincipal(["webhooks", "workflows", { webhookId }]),
};

export type PlainWebhook = PlainMessage<Webhook>;

export const useListWebhooks = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: webhookQueryKey.list(),
        queryFn: async (): Promise<PlainWebhook[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const request = new ListWebhooksRequest();
            const webhooks = await autoPaginate(
                (params) => api.webhookService.listWebhooks(params),
                request,
                (response) => response.webhooks,
            );
            return webhooks.map((w) => toPlainMessage(w));
        },
        throwOnError: false,
        retry: defaultRetry,
        enabled: !!user,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
};

export const useGetWebhook = (webhookId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: webhookQueryKey.get(webhookId),
        queryFn: async (): Promise<PlainWebhook> => {
            if (!webhookId) {
                throw new Error("webhookId is required");
            }
            const response = await api.webhookService.getWebhook(new GetWebhookRequest({ webhookId }));
            if (!response.webhook) {
                throw new Error("Webhook not found");
            }
            return toPlainMessage(response.webhook);
        },
        enabled: !!webhookId,
        retry: defaultRetry,
    });
};

export const useGetWebhookSecret = (webhookId?: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: webhookQueryKey.secret(webhookId),
        queryFn: async (): Promise<string> => {
            if (!webhookId) {
                throw new Error("webhookId is required");
            }
            const response = await api.webhookService.getWebhookSecret(new GetWebhookSecretRequest({ webhookId }));
            return response.secret;
        },
        enabled: !!webhookId,
        retry: defaultRetry,
    });
};

export const useRotateWebhookSecret = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (webhookId: string): Promise<string> => {
            const response = await api.webhookService.rotateWebhookSecret(
                new RotateWebhookSecretRequest({ webhookId }),
            );
            return response.secret;
        },
        onSuccess: (newSecret, webhookId) => {
            queryClient.setQueryData(webhookQueryKey.secret(webhookId), newSecret);
        },
    });
};

export type CreateWebhookParams = {
    name: string;
    description?: string;
    type: WebhookType;
    scope?: string;
    provider: WebhookProvider;
};

export const useCreateWebhook = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateWebhookParams): Promise<PlainWebhook> => {
            const response = await api.webhookService.createWebhook(
                new CreateWebhookRequest({
                    name: params.name,
                    description: params.description,
                    type: params.type,
                    scope: params.scope,
                    provider: params.provider,
                }),
            );
            if (!response.webhook) {
                throw new Error("Failed to create webhook");
            }
            return toPlainMessage(response.webhook);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: webhookQueryKey.list() });
        },
    });
};

export const useDeleteWebhook = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (webhookId: string): Promise<void> => {
            await api.webhookService.deleteWebhook(new DeleteWebhookRequest({ webhookId }));
        },
        onSuccess: (_, webhookId) => {
            // Remove from list cache
            queryClient.setQueryData<PlainWebhook[]>(webhookQueryKey.list(), (old) =>
                old?.filter((w) => w.id !== webhookId),
            );
            // Remove individual webhook cache
            queryClient.removeQueries({ queryKey: webhookQueryKey.get(webhookId) });
            // Remove secret cache
            queryClient.removeQueries({ queryKey: webhookQueryKey.secret(webhookId) });
        },
    });
};

export type UpdateWebhookParams = {
    webhookId: string;
    name?: string;
    description?: string;
    scope?: string;
};

export const useUpdateWebhook = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateWebhookParams): Promise<PlainWebhook> => {
            const response = await api.webhookService.updateWebhook(
                new UpdateWebhookRequest({
                    webhookId: params.webhookId,
                    name: params.name,
                    description: params.description,
                    scope: params.scope,
                }),
            );
            if (!response.webhook) {
                throw new Error("Failed to update webhook");
            }
            return toPlainMessage(response.webhook);
        },
        onSuccess: (webhook) => {
            // Update in list cache
            queryClient.setQueryData<PlainWebhook[]>(webhookQueryKey.list(), (old) =>
                old?.map((w) => (w.id === webhook.id ? webhook : w)),
            );
            // Update individual webhook cache
            queryClient.setQueryData(webhookQueryKey.get(webhook.id), webhook);
        },
    });
};

export const useCanDeleteWebhook = (webhookId: string | undefined) => {
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();
    const canDeleteWebhook = webhookId ? hasPermission(ResourceType.WEBHOOK, webhookId, "webhook:delete") : false;
    return {
        canDeleteWebhook,
        isLoading: isLoadingPermissions,
    };
};

export const useListWebhookWorkflows = (webhookId?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: webhookQueryKey.workflows(webhookId),
        queryFn: async (): Promise<PlainWorkflow[]> => {
            if (!webhookId) {
                throw new Error("webhookId is required");
            }

            const request = new ListWebhookWorkflowsRequest({ webhookId });
            const workflows = await autoPaginate(
                (params) => api.webhookService.listWebhookWorkflows(params),
                request,
                (response) => response.workflows,
            );
            return workflows.map((w) => toPlainMessage(w));
        },
        enabled: !!webhookId && !!user,
        retry: defaultRetry,
    });
};

export type WorkflowWithRunningStatus = {
    workflow: PlainWorkflow;
    isRunning: boolean;
};

export const useWebhookWorkflowsWithRunningStatus = (webhookId?: string, enabled = true) => {
    const api = useGitpodAPI();

    const { data: boundWorkflows, isLoading: isLoadingWorkflows } = useListWebhookWorkflows(
        enabled ? webhookId : undefined,
    );

    const executionQueries = useQueries({
        queries: (boundWorkflows ?? []).map((workflow) => ({
            queryKey: workflowQueryKey.executions.mostRecent(workflow.id),
            queryFn: async (): Promise<PlainWorkflowExecution | null> => {
                const response = await api.workflowService.listWorkflowExecutions({
                    filter: {
                        workflowIds: [workflow.id],
                        statusPhases: [
                            WorkflowExecutionPhase.PENDING,
                            WorkflowExecutionPhase.RUNNING,
                            WorkflowExecutionPhase.STOPPING,
                        ],
                    },
                    sort: new Sort({
                        field: "startedAt",
                        order: SortOrder.DESC,
                    }),
                    pagination: new PaginationRequest({ pageSize: 1 }),
                });

                if (response.workflowExecutions.length === 0) {
                    return null;
                }

                return toPlainMessage(response.workflowExecutions[0]);
            },
            enabled: enabled && !!boundWorkflows?.length,
            retry: defaultRetry,
        })),
    });

    const isLoadingExecutions = executionQueries.some((q) => q.isLoading);
    const isLoading = isLoadingWorkflows || isLoadingExecutions;

    const workflowsWithStatus: WorkflowWithRunningStatus[] = (boundWorkflows ?? []).map((workflow, index) => {
        const execution = executionQueries[index]?.data;
        const isRunning =
            execution?.status?.phase === WorkflowExecutionPhase.PENDING ||
            execution?.status?.phase === WorkflowExecutionPhase.RUNNING ||
            execution?.status?.phase === WorkflowExecutionPhase.STOPPING;
        return { workflow, isRunning };
    });

    const hasRunningExecutions = workflowsWithStatus.some((w) => w.isRunning);

    return {
        workflows: workflowsWithStatus,
        isLoading,
        hasRunningExecutions,
        hasBoundWorkflows: (boundWorkflows?.length ?? 0) > 0,
    };
};
